import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TreatmentType } from '@next/arable-lib/advice';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { map, Observable } from 'rxjs';
import { Environment } from 'src/environments/environment.model';

import {
  CreateOrderRequest,
  CreateOrderResponse,
  CropAidPackage,
  CropAidResponse,
  CustomerOrderPlan,
  CustomerOrderPlanResponse,
  FertilizerOrderPlanResponse,
  FertilizerStockData,
  GetCropAidPackagesResponse,
  GetCropAidsResponse,
  GetOrderLinesResponse,
  GetPackagesResponse,
  GetSalesOrderPeriodsResponse,
  OrderLine,
  OrderPlanV2,
  Package,
  ProductStockData,
  SalesOrderPeriod,
  SubmittedProductInventoryResponse,
  UpsertSubmittedProductInventoryRequest,
  UpsertCustomerProductStockRequest,
  UpsertFertilizerStockRequest,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class OrderplanningService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private readonly baseUrl = `${this.env.API_BASE_URL}/service/orderplanning/secure`;
  private readonly salesOrderBaseUrl = this.env.PRE_SALES_REBUILD_DONE
    ? `${this.env.APIM_BASE_URL}/arable-service`
    : `${this.env.API_BASE_URL}/service/orderplanning/secure`;
  private readonly arableServiceUrl = `${this.env.APIM_BASE_URL}/arable-service`;

  hasFinalizedAnyOrderLine(year: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/orderline/HasFinalizedAnyOrderLine/${year}`);
  }

  getCustomerOrderPlan(): Observable<CustomerOrderPlan[]> {
    return this.http
      .get<CustomerOrderPlanResponse>(`${this.baseUrl}/orderplan/customer`)
      .pipe(map((response) => response.result));
  }

  upsertCustomerProductInventory(year: number, productInventories: ProductStockData[]) {
    const request: UpsertCustomerProductStockRequest = {
      year,
      productInventories,
    };

    return this.http.post(`${this.baseUrl}/customerproductinventory`, request);
  }

  getSubmittedProductInventory(year: number): Observable<SubmittedProductInventoryResponse> {
    return this.http.get<SubmittedProductInventoryResponse>(`${this.baseUrl}/submittedproductinventory/${year}`);
  }

  upsertSubmittedProductInventory(year: number, productInventories: ProductStockData[]): Observable<void> {
    const request: UpsertSubmittedProductInventoryRequest = { productInventories };

    return this.http.put<void>(`${this.baseUrl}/submittedproductinventory/${year}`, request);
  }

  upsertFertilizerStock(cropYear: number, fertilizerStock: FertilizerStockData[]) {
    const request: UpsertFertilizerStockRequest = {
      cropYear,
      fertilizerStock,
    };

    return this.http.post(`${this.baseUrl}/customerfertilizerstock`, request);
  }

  optimizeOrderPlan() {
    return this.http.post(`${this.baseUrl}/orderplan/optimize/single-period`, {});
  }

  getOrderLines(year: number, treatmentType: TreatmentType): Observable<OrderLine[]> {
    let params = new HttpParams();
    params = params.set('treatmentType', treatmentType);

    return this.http
      .get<GetOrderLinesResponse>(`${this.baseUrl}/orderline/${year}`, { params })
      .pipe(map((orderLinesResponse) => orderLinesResponse.orderLines));
  }

  getCropAids(productType?: number): Observable<CropAidResponse[]> {
    let params = new HttpParams();
    if (productType !== undefined) {
      params = params.set('productType', productType.toString());
    }

    return this.http
      .get<GetCropAidsResponse>(`${this.arableServiceUrl}/crop-aid`, { params })
      .pipe(map((cropAidResponse) => cropAidResponse.cropAids));
  }

  getCropAidsByIds(cropAidIds: number[]): Observable<CropAidPackage[]> {
    let params = new HttpParams();
    for (const id of cropAidIds) {
      params = params.append('cropAidIds', id.toString());
    }

    return this.http
      .get<GetCropAidPackagesResponse>(`${this.baseUrl}/cropaid`, { params })
      .pipe(map((cropAidResponse) => cropAidResponse.cropAids));
  }

  getPackages(): Observable<Package[]> {
    return this.http
      .get<GetPackagesResponse>(`${this.baseUrl}/package`)
      .pipe(map((packageResponse) => packageResponse.packages));
  }

  getPackagesByCropAid(cropAidId: number): Observable<Package[]> {
    return this.http
      .get<GetPackagesResponse>(`${this.baseUrl}/package/crop-aid/${cropAidId}`)
      .pipe(map((packageResponse) => packageResponse.packages));
  }

  createOrder(year: number) {
    return this.http.post(`${this.baseUrl}/orderplan/customer-order`, { year });
  }

  getFertilizerOrderPlan(year: number): Observable<FertilizerOrderPlanResponse> {
    return this.http.get<FertilizerOrderPlanResponse>(`${this.baseUrl}/orderplan/fertiliser/${year}`);
  }

  deleteOrderLine(orderLineId: string, year: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/orderline/${orderLineId}/${year}`);
  }

  saveOrderLine(orderLine: OrderLine): Observable<OrderLine> {
    return this.http.post<OrderLine>(`${this.baseUrl}/orderline`, orderLine);
  }

  createFertilizerOrder(year: number, deliveryDate?: Date | null) {
    const request: CreateOrderRequest = {
      year,
      preferredOrderDate: deliveryDate || null,
    };
    return this.http.post<CreateOrderResponse>(`${this.baseUrl}/orderplan/fertiliser/create`, request);
  }

  getSalesOrderPeriods(year: number): Observable<SalesOrderPeriod[]> {
    const endpoint = this.env.PRE_SALES_REBUILD_DONE
      ? `salesorderperiod?year=${year}`
      : `salesorderperiod/year/${year}`;
    return this.http
      .get<GetSalesOrderPeriodsResponse>(`${this.salesOrderBaseUrl}/${endpoint}`)
      .pipe(map((response) => response.salesOrderPeriods));
  }

  getCustomerPreferences(year: number): Observable<{ preferedDeliveryPeriods: number[] }> {
    return this.http.get<{ preferedDeliveryPeriods: number[] }>(`${this.baseUrl}/customerpreference/${year}`);
  }

  getOrderplan(year: number): Observable<OrderPlanV2[]> {
    return this.http
      .get<{ result: OrderPlanV2[] }>(`${this.baseUrl}/orderplan/${year}`)
      .pipe(map((response) => response.result || []));
  }
}
